<template>
  <div
    class="mx-auto min-h-screen"
    :class="{
      [getThemeClass()]: true,
      'mobile-theme': isMiniScreen,
      'pc-theme': !isMiniScreen
    }"
    :style="containerStyle"
  >
    <div v-if="!$route.meta.selfHeader" ref="barRef">
      <PublicHeader
        v-if="!isInMp"
        class="w-full px-14px h-72px"
        :is-logo="true"
        :show-navbar="false"
        :show-search="false"
      />
      <div v-if="!isInMp" class="flex px-14px md:px-0 items-center gap-12px md:w-1200px mx-auto">
        <div class="size-24px bg-#F3F8FF rounded-6px flex flex-center cursor-pointer" @click="$router.back()">
          <IconLeft />
        </div>
        <div class="text-16px font-bold flex-none">
          {{ navTitle
            ? navTitle
            : $route.meta?.navTitle ? t($route.meta.navTitle as string) : ($route.meta?.title || '') }}
        </div>
      </div>
      <div v-if="!isInMp" class="md:w-1200px mx-auto mt-10px md:mt-20px">
        <div class="px-14px mt-8px">
          <a-divider :margin="0" />
        </div>
      </div>
    </div>
    <USpin :tip="indicatorState.tip" :loading="indicatorState.loading" fit-view>
      <div class="w-full md:w-1200px md:mx-auto">
        <router-view v-slot="{ Component, route }">
          <transition name="fade" mode="out-in" appear>
            <component
              :is="getComponent(Component, route)"
              v-if="route.meta.ignoreCache"
              :key="route.meta.reuse ? route.path : route.fullPath"
            />
          </transition>
          <keep-alive :max="10">
            <component
              :is="getComponent(Component, route)"
              v-if="!route.meta.ignoreCache"
              :key="route.meta.reuse ? route.path : route.fullPath"
            />
          </keep-alive>
        </router-view>
      </div>
    </USpin>
  </div>
</template>

<script lang="ts" setup>
import { useIndicatorState } from '@/hooks/useIndicator'
import { useSysStore } from '@/store'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const sysStore = useSysStore()
const route = useRoute()
const { t } = useI18n()

function getComponent(Component: any, route: any) {
  return Component
}
function getIsMiniScreen() {
  return window.innerWidth <= 768
}

const isMiniScreen = ref(true)
const isInMp = computed(() => sysStore.isInMp)
const navTitle = computed(() => sysStore.navTitle)

onMounted(() => {
  isMiniScreen.value = getIsMiniScreen()
})

const indicatorState = useIndicatorState()

const barRef = ref<HTMLElement | null>(null)
const barHeight = ref(0)
let resizeObserver: ResizeObserver | null = null

function updateBarHeight() {
  barHeight.value = barRef.value?.offsetHeight ?? 0
}

watch(barRef, el => {
  resizeObserver?.disconnect()
  if (el) {
    resizeObserver = new ResizeObserver(updateBarHeight)
    resizeObserver.observe(el)
    updateBarHeight()
  } else {
    barHeight.value = 0
  }
}, { immediate: true, flush: 'post' })

onUnmounted(() => {
  resizeObserver?.disconnect()
})

const containerStyle = computed(() => ({
  ...(route.meta.bgWhite ? { background: '#fff' } : {}),
  '--bar-height': `${barHeight.value}px`
}))

function getThemeClass() {
  if(route.meta.theme) {
    return route.meta.theme as string
  }
  return 'default-theme'
}
</script>

<style lang="less">
.full-height-in-layout {
  height: calc(100vh - var(--bar-height));
}
.default-theme {
  background: url(/images/indexbg.png) no-repeat;
  background-size: cover;
  background-position: left top;
  --shadow2: 0px 3px 15px 1px rgba(13,34,92,0.05);
  --card-padding1: 48px 36px;
  --card-padding2: 10px 14px;
  --card-padding3: 8px 12px;

  --task-content-bg: transparent;
  --task-content-shadow: none;
  --task-card-padding: var(--card-padding1);
  --task-card-padding1: var(--card-padding3);
  --task-card-bg: rgba(246,248,251,.8);
  --task-card-radius: 10px;
  --task-file-bg: #fff;
  --task-file-radius: 4px;
}
.mobile-theme {
  --task-content-bg: #fff;
  --task-content-shadow: var(--shadow2);
  --task-card-padding: var(--card-padding2);
  --task-card-padding1: var(--card-padding3);
  --task-card-bg: transparent;
  --task-file-bg: transparent;
}
</style>
<style scoped lang="less">
.header {
  :deep(.arco-page-header-wrapper) {
    padding: 0 14px;
  }
}

</style>
